import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { useRepairCase } from '../lib/repair-kb-client';
import {
  DEVICE_FAMILIES, RESULT_OPTIONS, MEASUREMENT_TYPES,
  ERROR_SOURCES, COMPONENT_TYPES, FAILURE_MODES,
  fetchLinkedReferences,
  fetchTaxonomy,
  findManufacturer,
  type KbTaxonomy,
} from '../lib/repair-kb-client';
import type { RepairMedia, LinkedReference } from '../lib/repair-kb-client';
import {
  ChevronRight, Wrench, Cpu, Calendar, AlertTriangle,
  BarChart3, Zap, Package, Image, FileText, Loader, X,
} from '../lib/icons';

// ── Helpers ───────────────────────────────────────────────────────────────────

function deviceLabel(family: string) {
  return DEVICE_FAMILIES.find(d => d.value === family)?.label ?? family;
}

function resultLabel(result: string) {
  return RESULT_OPTIONS.find(r => r.value === result)?.label ?? result;
}

function resultColor(result: string) {
  switch (result) {
    case 'repaired':           return 'text-green-400 bg-green-900/30 border-green-800';
    case 'partially-repaired': return 'text-yellow-400 bg-yellow-900/30 border-yellow-800';
    case 'no-fix':             return 'text-red-400 bg-red-900/30 border-red-800';
    case 'board-donor':        return 'text-orange-400 bg-orange-900/30 border-orange-800';
    case 'further-diagnosis':  return 'text-blue-400 bg-blue-900/30 border-blue-800';
    default: return 'text-gray-400 bg-gray-800 border-gray-700';
  }
}

function measurementTypeLabel(t: string) {
  return MEASUREMENT_TYPES.find(m => m.value === t)?.label ?? t;
}

function errorSourceLabel(s: string) {
  return ERROR_SOURCES.find(e => e.value === s)?.label ?? s;
}

function componentTypeLabel(t: string) {
  return COMPONENT_TYPES.find(c => c.value === t)?.label ?? t;
}

function failureModeLabel(m: string) {
  return FAILURE_MODES.find(f => f.value === m)?.label ?? m;
}

function formatDate(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function prose(text: string) {
  return text.split('\n').filter(Boolean).map((line, i) => (
    <p key={i} className="text-gray-300 leading-relaxed mb-3 last:mb-0">{line}</p>
  ));
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-800">
        <span className="text-[#FF1744]">{icon}</span>
        <h2 className="text-lg font-bold text-white">{title}</h2>
      </div>
      {children}
    </section>
  );
}

// ── Photo lightbox ────────────────────────────────────────────────────────────

function Lightbox({ media, index, onClose, onPrev, onNext }: {
  media: RepairMedia[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const item = media[index];
  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex flex-col"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <span className="text-gray-400 text-sm">{index + 1} / {media.length}</span>
        <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Image */}
      <div className="flex-1 flex items-center justify-center px-4 min-h-0" onClick={e => e.stopPropagation()}>
        <button onClick={onPrev} className="p-3 text-gray-500 hover:text-white transition-colors flex-shrink-0 disabled:opacity-20" disabled={index === 0}>
          <ChevronRight className="h-8 w-8 rotate-180" />
        </button>
        <img
          src={item.public_url}
          alt={item.caption || `Photo ${index + 1}`}
          className="max-h-full max-w-full object-contain flex-1"
        />
        <button onClick={onNext} className="p-3 text-gray-500 hover:text-white transition-colors flex-shrink-0 disabled:opacity-20" disabled={index === media.length - 1}>
          <ChevronRight className="h-8 w-8" />
        </button>
      </div>

      {/* Caption */}
      {item.caption && (
        <div className="px-6 py-4 text-center flex-shrink-0" onClick={e => e.stopPropagation()}>
          <p className="text-gray-300 text-sm max-w-2xl mx-auto">{item.caption}</p>
        </div>
      )}
    </div>
  );
}

// ── Photo gallery ─────────────────────────────────────────────────────────────

function PhotoGallery({ media }: { media: RepairMedia[] }) {
  const [lightbox, setLightbox] = useState<number | null>(null);
  const sorted = [...media].sort((a, b) => {
    if (a.is_featured && !b.is_featured) return -1;
    if (!a.is_featured && b.is_featured) return 1;
    return a.sort_order - b.sort_order;
  });

  return (
    <Section icon={<Image className="h-5 w-5" />} title="Photos">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {sorted.map((m, i) => (
          <button
            key={m.id}
            onClick={() => setLightbox(i)}
            className="group relative aspect-video bg-gray-950 rounded-lg overflow-hidden border border-gray-800 hover:border-gray-600 transition-all"
          >
            <img
              src={m.public_url}
              alt={m.caption || `Photo ${i + 1}`}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            {m.is_featured && (
              <span className="absolute top-1.5 left-1.5 text-[9px] font-bold bg-[#FF1744] text-white px-1.5 py-0.5 rounded">FEATURED</span>
            )}
            {m.caption && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 translate-y-full group-hover:translate-y-0 transition-transform">
                <p className="text-white text-[10px] line-clamp-2">{m.caption}</p>
              </div>
            )}
          </button>
        ))}
      </div>

      {lightbox !== null && (
        <Lightbox
          media={sorted}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox(i => Math.max(0, (i ?? 0) - 1))}
          onNext={() => setLightbox(i => Math.min(sorted.length - 1, (i ?? 0) + 1))}
        />
      )}
    </Section>
  );
}

// ── Linked board references ───────────────────────────────────────────────────

function LinkedReferencesSection({ caseId }: { caseId: string }) {
  const [refs, setRefs] = useState<LinkedReference[] | null>(null);

  useEffect(() => {
    fetchLinkedReferences(caseId)
      .then(setRefs)
      .catch(() => setRefs([]));
  }, [caseId]);

  if (!refs || refs.length === 0) return null;

  return (
    <Section icon={<FileText className="h-5 w-5" />} title="Board References">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {refs.map(ref => {
          const img = ref.repair_reference_media
            ?.slice().sort((a, b) => a.sort_order - b.sort_order)[0]?.public_url;
          const href = `/repair-database/ref/${ref.slug ?? ref.id}`;
          return (
            <Link
              key={ref.id}
              to={href}
              className="flex gap-3 bg-gray-900 border border-[#00BFFF]/30 hover:border-[#00BFFF]/70 rounded-xl p-3 transition-all group"
            >
              {img && (
                <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden bg-gray-800 border border-gray-700">
                  <img src={img} alt={ref.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#00BFFF] mb-0.5">Board Reference</p>
                <p className="text-sm font-semibold text-[#E0E0E0] leading-snug truncate">{ref.title}</p>
                {(ref.device_family || ref.board_revision) && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {[ref.device_family, ref.board_revision].filter(Boolean).join(' · ')}
                  </p>
                )}
                {ref.verification_status && (
                  <span className="text-[10px] font-bold text-[#39FF14] uppercase tracking-wide">{ref.verification_status}</span>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-gray-600 group-hover:text-[#00BFFF] shrink-0 self-center transition-colors" />
            </Link>
          );
        })}
      </div>
    </Section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function RepairCasePage() {
  const { deviceSlug, boardSlug, caseSlug } = useParams<{ deviceSlug: string; boardSlug: string; caseSlug: string }>();
  const { repairCase: c, loading, error } = useRepairCase(caseSlug ?? '', false);
  const [taxonomy, setTaxonomy] = useState<KbTaxonomy>({ manufacturers: [], device_families: [], board_revisions: [] });
  useEffect(() => { fetchTaxonomy().then(setTaxonomy).catch(() => {}); }, []);

  if (loading) {
    return (
      <div className="bg-[#0D0D0D] min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader className="h-5 w-5 animate-spin" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  if (error || !c) {
    return (
      <div className="bg-[#0D0D0D] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-12 w-12 text-gray-800 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">Repair case not found.</p>
          <Link to="/repair-database" className="text-[#FF1744] hover:text-[#FF1744]/80 text-sm transition-colors">
            ← Back to Repair Database
          </Link>
        </div>
      </div>
    );
  }

  const errors       = c.repair_case_errors ?? [];
  const measurements = c.repair_case_measurements ?? [];
  const injections   = c.repair_case_voltage_injections ?? [];
  const components   = c.repair_case_components ?? [];
  const media        = c.repair_case_media ?? [];

  // Taxonomy-driven breadcrumb labels
  const txFamily = c.device_family_id
    ? taxonomy.device_families.find(f => f.id === c.device_family_id)
    : undefined;
  const txMfr  = txFamily ? findManufacturer(taxonomy, txFamily.manufacturer_id) : undefined;
  const txBoard = c.board_revision_id
    ? taxonomy.board_revisions.find(b => b.id === c.board_revision_id)
    : undefined;

  const canonicalPath = deviceSlug && boardSlug && caseSlug
    ? `/repair-database/${deviceSlug}/${boardSlug}/${caseSlug}`
    : `/repair-database/${c.slug}`;

  const jsonLD = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: c.seo_title || c.title,
    description: c.seo_description || `Board-level repair documentation for ${deviceLabel(c.device_family)}${c.board_revision ? ` (${c.board_revision})` : ''}.`,
    url: `https://gamefixsolutions.com${canonicalPath}`,
    datePublished: c.published_at ?? c.created_at,
    dateModified: c.updated_at,
    author: { '@type': 'Organization', name: 'GameFix Solutions', url: 'https://gamefixsolutions.com' },
    publisher: { '@type': 'Organization', name: 'GameFix Solutions', url: 'https://gamefixsolutions.com' },
    ...(c.featured_image_url ? { image: c.featured_image_url } : {}),
  };

  return (
    <div className="bg-[#0D0D0D] min-h-screen text-[#E0E0E0]">
      <SEOHead
        title={c.seo_title || c.title || `${deviceLabel(c.device_family)} Repair — GameFix Solutions`}
        description={c.seo_description || `Board-level repair case: ${c.symptoms_summary.join(', ')}. ${c.root_cause ? `Root cause: ${c.root_cause}` : ''}`}
        keywords={[
          deviceLabel(c.device_family),
          c.device_model,
          c.board_revision,
          ...c.tags,
          ...errors.map(e => e.error_code),
          'board level repair',
          'GameFix Solutions',
        ].filter(Boolean).join(', ')}
        canonical={`https://gamefixsolutions.com${canonicalPath}`}
        jsonLD={jsonLD}
      />

      {/* ── Breadcrumb ───────────────────────────────────────────────────── */}
      <div className="border-b border-gray-800/60">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <nav className="flex items-center gap-1.5 text-xs text-gray-500 flex-wrap">
            <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/repair-database" className="hover:text-gray-300 transition-colors">Repair Database</Link>
            {txMfr && <><ChevronRight className="h-3 w-3" /><span className="text-gray-500">{txMfr.name}</span></>}
            {deviceSlug && (
              <>
                <ChevronRight className="h-3 w-3" />
                <Link to={`/repair-database/${deviceSlug}`} className="hover:text-gray-300 transition-colors">
                  {txFamily?.name ?? deviceLabel(c.device_family)}
                </Link>
              </>
            )}
            {deviceSlug && boardSlug && (
              <>
                <ChevronRight className="h-3 w-3" />
                <Link to={`/repair-database/${deviceSlug}/${boardSlug}`} className="hover:text-gray-300 transition-colors">
                  {txBoard?.name ?? c.board_revision ?? boardSlug.toUpperCase()}
                </Link>
              </>
            )}
            <ChevronRight className="h-3 w-3" />
            <span className="text-gray-400 truncate max-w-xs">{c.title || deviceLabel(c.device_family)}</span>
          </nav>
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="border-b border-gray-800/60 bg-gradient-to-b from-gray-900/40 to-transparent">
        <div className="max-w-4xl mx-auto px-6 py-10 md:py-14">
          {/* Device line */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm font-bold text-[#FF1744]">{deviceLabel(c.device_family)}</span>
            {c.device_model && <span className="text-sm text-gray-500">{c.device_model}</span>}
            {c.board_revision && (
              <span className="text-sm font-mono bg-gray-800 text-gray-300 px-2 py-0.5 rounded border border-gray-700">{c.board_revision}</span>
            )}
            {c.result && (
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${resultColor(c.result)}`}>
                {resultLabel(c.result)}
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-black text-white mb-4 leading-tight">
            {c.title || `${deviceLabel(c.device_family)} Repair`}
          </h1>

          {/* Symptom chips */}
          {c.symptoms_summary.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-5">
              {c.symptoms_summary.map(s => (
                <span key={s} className="text-xs bg-gray-800 text-gray-300 px-2.5 py-1 rounded-full">{s}</span>
              ))}
            </div>
          )}

          {/* Category + date */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            {c.repair_categories.map(cat => (
              <span key={cat} className="text-[#FF1744]/70 bg-[#FF1744]/10 border border-[#FF1744]/20 px-2 py-0.5 rounded-full">{cat}</span>
            ))}
            {c.published_at && (
              <span className="flex items-center gap-1 ml-auto">
                <Calendar className="h-3 w-3" />{formatDate(c.published_at)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Two-column layout ─────────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* ── Main content ──────────────────────────────────────────────── */}
          <div className="lg:col-span-2">

            {/* Symptoms */}
            {c.detailed_symptoms && (
              <Section icon={<AlertTriangle className="h-5 w-5" />} title="Symptoms">
                <div>{prose(c.detailed_symptoms)}</div>
              </Section>
            )}

            {/* Diagnostic Codes */}
            {errors.length > 0 && (
              <Section icon={<FileText className="h-5 w-5" />} title="Diagnostic Codes">
                <div className="space-y-2">
                  {errors.map((e, i) => (
                    <div key={e.id ?? i} className="flex items-start gap-3 bg-gray-900 border border-gray-800 rounded-lg p-3">
                      <div className="flex-shrink-0">
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{errorSourceLabel(e.source)}</span>
                        <p className="font-mono font-bold text-[#FF1744] text-sm mt-0.5">{e.error_code}</p>
                      </div>
                      {e.notes && <p className="text-gray-400 text-sm mt-0.5">{e.notes}</p>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Board Measurements */}
            {measurements.length > 0 && (
              <Section icon={<BarChart3 className="h-5 w-5" />} title="Board Measurements">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-800">
                        <th className="pb-2 pr-4 font-medium">Reference</th>
                        <th className="pb-2 pr-4 font-medium">Type</th>
                        <th className="pb-2 pr-4 font-medium">Expected</th>
                        <th className="pb-2 pr-4 font-medium">Measured</th>
                        <th className="pb-2 font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {measurements.map((m, i) => (
                        <tr key={m.id ?? i} className={`border-b border-gray-800/60 ${m.is_verified_reference ? 'bg-green-900/10' : ''}`}>
                          <td className="py-2.5 pr-4 font-mono text-[#E0E0E0]">
                            {m.reference_line}
                            {m.is_verified_reference && (
                              <span className="ml-1.5 text-[9px] text-green-400 border border-green-800 px-1 py-0.5 rounded align-middle">REF</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-4 text-gray-400 text-xs">{measurementTypeLabel(m.measurement_type)}</td>
                          <td className="py-2.5 pr-4 font-mono text-gray-400 text-xs">{m.expected_value}{m.expected_value && m.units ? ` ${m.units}` : ''}</td>
                          <td className={`py-2.5 pr-4 font-mono text-xs font-semibold ${m.measured_value ? 'text-[#FF1744]' : 'text-gray-600'}`}>
                            {m.measured_value || '—'}{m.measured_value && m.units ? ` ${m.units}` : ''}
                          </td>
                          <td className="py-2.5 text-gray-500 text-xs max-w-[160px]">{m.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            {/* Voltage Injection */}
            {injections.length > 0 && (
              <Section icon={<Zap className="h-5 w-5" />} title="Voltage Injection">
                <div className="space-y-3">
                  {injections.map((inj, i) => (
                    <div key={inj.id ?? i} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                      <div className="flex flex-wrap gap-4 mb-2">
                        <div>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Injection Point</p>
                          <p className="font-mono text-sm text-[#E0E0E0]">{inj.injection_point}</p>
                        </div>
                        {inj.injected_voltage && (
                          <div>
                            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Voltage</p>
                            <p className="font-mono text-sm text-[#FF1744]">{inj.injected_voltage} V</p>
                          </div>
                        )}
                        {inj.current_limit && (
                          <div>
                            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Current Limit</p>
                            <p className="font-mono text-sm text-[#E0E0E0]">{inj.current_limit} A</p>
                          </div>
                        )}
                        {inj.component_heated && (
                          <div>
                            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Component Heated</p>
                            <p className="font-mono text-sm text-[#E0E0E0]">{inj.component_heated}</p>
                          </div>
                        )}
                      </div>
                      {inj.result && <p className="text-gray-400 text-sm border-t border-gray-800 pt-2 mt-2">{inj.result}</p>}
                      {inj.notes && <p className="text-gray-500 text-xs mt-1">{inj.notes}</p>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Diagnosis / Root Cause */}
            {c.root_cause && (
              <Section icon={<Cpu className="h-5 w-5" />} title="Diagnosis">
                <div>{prose(c.root_cause)}</div>
              </Section>
            )}

            {/* Failed Components */}
            {components.length > 0 && (
              <Section icon={<Package className="h-5 w-5" />} title="Failed Components">
                <div className="space-y-2">
                  {components.map((comp, i) => (
                    <div key={comp.id ?? i} className="flex items-start gap-3 bg-gray-900 border border-gray-800 rounded-lg p-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-mono font-bold text-[#E0E0E0] text-sm">{comp.component_ref}</span>
                          <span className="text-[10px] bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded">{componentTypeLabel(comp.component_type)}</span>
                          <span className="text-[10px] bg-red-900/30 text-red-400 border border-red-800 px-1.5 py-0.5 rounded">{failureModeLabel(comp.failure_mode)}</span>
                        </div>
                        {comp.notes && <p className="text-gray-400 text-sm">{comp.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Repair Performed */}
            {c.repair_summary && (
              <Section icon={<Wrench className="h-5 w-5" />} title="Repair Performed">
                <div>{prose(c.repair_summary)}</div>
                {c.components_replaced && (
                  <div className="mt-4 bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Components Replaced</p>
                    <p className="text-gray-300 text-sm">{c.components_replaced}</p>
                  </div>
                )}
                {c.jumper_trace_repair && (
                  <div className="mt-3 bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Jumper / Trace Repair</p>
                    <p className="text-gray-300 text-sm">{c.jumper_trace_repair}</p>
                  </div>
                )}
              </Section>
            )}

            {/* Post-Repair Testing */}
            {c.post_repair_testing && (
              <Section icon={<BarChart3 className="h-5 w-5" />} title="Post-Repair Testing">
                <div>{prose(c.post_repair_testing)}</div>
              </Section>
            )}

            {/* Photos */}
            {media.length > 0 && <PhotoGallery media={media} />}

            {/* Public Notes */}
            {c.public_notes && (
              <Section icon={<FileText className="h-5 w-5" />} title="Notes">
                <div>{prose(c.public_notes)}</div>
              </Section>
            )}

            {/* Linked board references */}
            <LinkedReferencesSection caseId={c.id} />
          </div>

          {/* ── Sidebar ───────────────────────────────────────────────────── */}
          <aside className="space-y-6">

            {/* Quick facts */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Case Details</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500 text-xs mb-0.5">Device</p>
                  <p className="text-[#E0E0E0] font-semibold">{deviceLabel(c.device_family)}</p>
                </div>
                {c.device_model && (
                  <div>
                    <p className="text-gray-500 text-xs mb-0.5">Model</p>
                    <p className="text-[#E0E0E0]">{c.device_model}</p>
                  </div>
                )}
                {c.board_revision && (
                  <div>
                    <p className="text-gray-500 text-xs mb-0.5">Board Revision</p>
                    <p className="font-mono text-[#E0E0E0]">{c.board_revision}</p>
                  </div>
                )}
                {c.result && (
                  <div>
                    <p className="text-gray-500 text-xs mb-0.5">Outcome</p>
                    <span className={`inline-flex text-xs font-bold px-2.5 py-0.5 rounded-full border ${resultColor(c.result)}`}>
                      {resultLabel(c.result)}
                    </span>
                  </div>
                )}
                {c.published_at && (
                  <div>
                    <p className="text-gray-500 text-xs mb-0.5">Published</p>
                    <p className="text-[#E0E0E0]">{formatDate(c.published_at)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Tags */}
            {c.tags.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Tags</h3>
                <div className="flex flex-wrap gap-1.5">
                  {c.tags.map(tag => (
                    <span key={tag} className="text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-b from-gray-900 to-gray-900/50 border border-gray-800 rounded-2xl p-5">
              <p className="text-xs font-semibold text-[#FF1744] uppercase tracking-wide mb-2">Need This Fixed?</p>
              <p className="text-gray-400 text-sm mb-4">Our technicians handle board-level repairs at our San Diego and Temecula locations.</p>
              <div className="space-y-2">
                <Link
                  to="/book-repair"
                  className="flex items-center justify-center gap-2 bg-[#FF1744] hover:bg-[#D50000] text-white font-bold px-4 py-2.5 rounded-xl transition-colors text-sm"
                >
                  Schedule a Repair <ChevronRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/mail-in-repairs"
                  className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-4 py-2.5 rounded-xl border border-gray-700 transition-colors text-sm"
                >
                  Mail In Your Console
                </Link>
              </div>
            </div>

            {/* Back link */}
            <Link
              to="/repair-database"
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#E0E0E0] transition-colors"
            >
              <ChevronRight className="h-4 w-4 rotate-180 flex-shrink-0" />
              Back to Repair Database
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
}
